import { IonButtons, IonIcon, IonModal } from '@ionic/react';
import { AnimatedProps, animated, useSpring } from '@react-spring/web';
import { resizeOutline } from 'ionicons/icons';
import {
  ComponentProps,
  ReactElement,
  ReactNode,
  forwardRef,
  useMemo,
  useRef,
  useState,
} from 'react';
import { mergeRefs } from 'react-merge-refs';
import { useIonicBreakpoints } from '../../util/screen-size';
import { BaseButton } from '../button/base-button';
import { useModalSizeObserver } from './modal.hooks';

type IonModalProps = ComponentProps<typeof IonModal>;

interface FullScreenIonModalProps extends IonModalProps {
  isFullScreen?: boolean;
}

const FullScreenIonModal = forwardRef<
  HTMLIonModalElement,
  FullScreenIonModalProps
>((props: FullScreenIonModalProps, ref): ReactElement => {
  const { isOpen, children, isFullScreen = false, ...restProps } = props;

  const canGoFullScreen = useCanGoFullScreen();

  const modalRef = useRef<HTMLIonModalElement>(null);
  const mergedModalRef = mergeRefs([ref, modalRef]);

  const initialModalSize = useModalSizeObserver({
    modalRef,
    isOpen: isOpen ?? false,
    isFullScreen,
    canGoFullScreen,
  });

  const defaultWidth = 600;
  const defaultHeight = 600;
  const collapsedWidth =
    initialModalSize.width > 0 ? initialModalSize.width : defaultWidth;
  const collapsedHeight =
    initialModalSize.height > 0 ? initialModalSize.height : defaultHeight;
  const targetWidth = window.innerWidth;
  const targetHeight = window.innerHeight;

  const animatedProps = useSpring({
    to: {
      width: isFullScreen ? targetWidth : collapsedWidth,
      height: isFullScreen ? targetHeight : collapsedHeight,
    },
    from: {
      width: collapsedWidth,
      height: collapsedHeight,
    },
    reset: !isOpen,
    config: { tension: 150, friction: 30 },
  });

  const AnimatedIonModal = animated(IonModal);

  const dynamicStyles = useMemo(() => {
    if (canGoFullScreen && isOpen) {
      const styles: AnimatedProps<NonNullable<IonModalProps['style']>> = {
        '--width': animatedProps.width.to((w) => `${Math.round(w)}px`),
        '--height': animatedProps.height.to((h) => `${Math.round(h)}px`),
        '--max-width': animatedProps.width.to((w) => `${Math.round(w)}px`),
        '--max-height': animatedProps.height.to((h) => `${Math.round(h)}px`),
      };

      return styles;
    }
    return undefined;
  }, [canGoFullScreen, isOpen, animatedProps.width, animatedProps.height]);

  return (
    <AnimatedIonModal
      {...restProps}
      isOpen={isOpen}
      ref={mergedModalRef}
      style={dynamicStyles}
    >
      {children}
    </AnimatedIonModal>
  );
});

FullScreenIonModal.displayName = 'FullScreenIonModal';

type ToggleFullScreenFn = (params: { forceSize?: 'standard' | 'full' }) => void;

interface UseFullScreenIonModalButtonReturn {
  isFullScreen: boolean;
  toggleFullScreen: ToggleFullScreenFn;
  buttonComponent: ReactNode;
}

const useFullScreenIonModalButton = () => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const canGoFullSceen = useCanGoFullScreen();

  const toggleFullScreen: ToggleFullScreenFn = ({ forceSize }) => {
    const forceFullScreen = forceSize === 'full';
    setIsFullScreen((prev) => forceFullScreen ?? !prev);
  };

  const buttonComponent = useMemo(() => {
    if (!canGoFullSceen) {
      return null;
    }

    return (
      <IonButtons slot={'end'}>
        <BaseButton
          buttonProps={{
            onClick: () => {
              setIsFullScreen((prev) => !prev);
            },
            color: 'primary',
          }}
        >
          <IonIcon icon={resizeOutline} />
        </BaseButton>
      </IonButtons>
    );
  }, [canGoFullSceen]);

  const result: UseFullScreenIonModalButtonReturn = {
    isFullScreen,
    toggleFullScreen,
    buttonComponent,
  };

  return result;
};

const useCanGoFullScreen = () => {
  const { isMd } = useIonicBreakpoints();

  return isMd;
};

export { FullScreenIonModal, useFullScreenIonModalButton };
